import { RequestStatus } from '../../../../../shared/enums/request-status';
import { SubscriptionPlans } from '../../../../../shared/utils/subscription-plans';
import { formatNumber } from '../../../../sequence/components/sequence-single-content/utils/helper';
import {
  AiCreditPlan,
  CalculateAiCreditsPricingsResponse,
} from '../types/ai-credits';
import {
  CalculateCreditsPricesResponse,
  CreditsPlan,
  PurchaseCreditsType,
} from '../types/purchase-credits';

export const getIsUserSubscribed = (planCode: SubscriptionPlans) =>
  planCode !== SubscriptionPlans.FREE && planCode !== SubscriptionPlans.TRIAL;

export const getIsLeadFinderUserSubscribed = (planCode: SubscriptionPlans) =>
  planCode !== SubscriptionPlans.LEAD_FINDER_FREE &&
  planCode !== SubscriptionPlans.LEAD_FINDER_TRIAL;

export const getIsInboxAnalyzerUserSubscribed = (planCode: SubscriptionPlans) =>
  planCode !== SubscriptionPlans.INBOX_ANALYZER_FREE &&
  planCode !== SubscriptionPlans.INBOX_ANALYZER_TRIAL;

export const isPageLoading = (
  sendGetPlansRequestStatus,
  sendGetAccountSubscriptionRequestStatus,
  sendGetConnectedUsersAndEmailAccountsRequestStatus,
) =>
  sendGetPlansRequestStatus === RequestStatus.Ideal ||
  sendGetPlansRequestStatus === RequestStatus.Pending ||
  sendGetAccountSubscriptionRequestStatus === RequestStatus.Ideal ||
  sendGetAccountSubscriptionRequestStatus === RequestStatus.Pending ||
  sendGetConnectedUsersAndEmailAccountsRequestStatus === RequestStatus.Ideal ||
  sendGetConnectedUsersAndEmailAccountsRequestStatus === RequestStatus.Pending;

export const isRequestPending = (status) => status === RequestStatus.Pending;

// ** Constants **
export const CREDITS_PURCHASE_OPTIONS = [
  5000,
  10000,
  25000,
  50000,
  100000,
  250000,
  500000,
  1000000,
];

// Ai Credit Purchase Plans Helper Function
export const aiCreditPurchasePlans = (
  calculateAiCreditsPricingsResponse: CalculateAiCreditsPricingsResponse,
): AiCreditPlan[] => {
  const entries = Object?.entries(
    calculateAiCreditsPricingsResponse || {},
  )?.map(([creditsStr, amount], index) => {
    const credits = Number(creditsStr);
    return {
      id: `${index + 1}`,
      amount,
      credits,
      name: formatNumber(credits),
      isBestValue: credits === 25000 ? 1 : 0,
    };
  });

  return entries;
};

export const CreditPurchaseOptions: Record<PurchaseCreditsType, number[]> = {
  [PurchaseCreditsType.EV_CREDITS]: [
    5000,
    10000,
    25000,
    50000,
    100000,
    250000,
    500000,
    1000000,
  ],
  [PurchaseCreditsType.AI_CREDITS]: [
    5000,
    10000,
    25000,
    50000,
    100000,
    250000,
    500000,
    1000000,
  ],
  [PurchaseCreditsType.LF_CREDITS]: [
    1000,
    5000,
    10000,
    15000,
    20000,
    25000,
    50000,
    100000,
  ],
};

export const CreditsPlanBestValue: Record<PurchaseCreditsType, number> = {
  [PurchaseCreditsType.EV_CREDITS]: 25000,
  [PurchaseCreditsType.AI_CREDITS]: 25000,
  [PurchaseCreditsType.LF_CREDITS]: 20000,
};

export const generatePurchaseCreditsPlans = (
  calculateCreditsPricesResponse: CalculateCreditsPricesResponse,
  type: PurchaseCreditsType,
): CreditsPlan[] => {
  const planEntries = Object?.entries(calculateCreditsPricesResponse || {});
  const plans: CreditsPlan[] = planEntries.map(
    ([creditsStr, amount], index) => {
      const credits = Number(creditsStr);
      return {
        id: `${index + 1}`,
        amount,
        credits,
        name: formatNumber(credits),
        isBestValue: credits === CreditsPlanBestValue[type] ? 1 : 0,
      };
    },
  );
  return plans;
};
